import { useEffect, useState, useMemo } from "react";
import { useAuth } from "@/lib/auth";
import { getProfile, saveProfile, isAdminUser, getSetupState } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import {
  LogOut,
  Rocket,
  ShieldCheck,
  Mail,
  Phone,
  MapPin,
  Building2,
  Loader2,
  Save,
  Pencil,
  Check,
  Plus,
  Minus,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import EmailSending from "@/components/EmailSending";
import MarketingHub from "@/components/MarketingHub";
import TwilioSettings from "@/components/TwilioSettings";
import SocialSettings from "@/components/SocialSettings";
import InstagramImportPanel from "@/components/InstagramImportPanel";
import DealerInstagramSettings from "@/components/DealerInstagramSettings";
import ImageUpload from "@/components/ImageUpload";
import ChangePasswordCard from "@/components/ChangePasswordCard";
import GalleryTab from "@/components/GalleryTab";
import DealerDashboard from "@/components/DealerDashboard";
import { INPUT_CLS, Field, Grid2, HandleBlock, BioBlock, AboutUsBlock, HomeWidgetBlock, HomeTabsBlock, HomeTabToggle, WebSocialBlock, PaymentHandlesBlock } from "@/components/profile/ProfileBlocks";
import SetupTab from "@/components/setup/SetupTab";
import ActivationPopup from "@/components/setup/ActivationPopup";
import ActivationReport from "@/components/setup/ActivationReport";
import IntegrationsSection from "@/components/IntegrationsSection";
import { SetupReportInline, useSetupReport } from "@/components/setup/SetupReportDialog";

export default function Profile() {
  const { user, logout, adoptUser } = useAuth();
  const recId = user?.user_record_id || user?.record_id;

  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sameShip, setSameShip] = useState(true);

  useEffect(() => {
    if (!recId) return;
    setLoading(true);
    getProfile(recId)
      .then((res) => {
        setProfile(res.profile);
        setForm(res.profile);
        setSameShip(
          res.profile.ship_same_as_billing === 1 ||
            (res.profile.ship_same_as_billing !== 0 && !res.profile.ship_address)
        );
      })
      .catch(() => toast.error("Could not load profile"))
      .finally(() => setLoading(false));
  }, [recId]);

  const isDirty = useMemo(() => {
    if (!form || !profile) return false;
    return Object.keys(form).some((k) => form[k] !== profile[k]);
  }, [form, profile]);

  function bind(field) {
    return {
      value: form?.[field] ?? "",
      onChange: (e) =>
        setForm((f) => ({ ...f, [field]: e.target.value })),
    };
  }

  async function save() {
    if (!isDirty || !recId) return;
    setSaving(true);
    try {
      const payload = { user_record_id: recId };
      Object.keys(form).forEach((k) => {
        if (form[k] !== profile[k]) payload[k] = form[k] ?? "";
      });
      const res = await saveProfile(payload);
      setProfile(res.profile);
      setForm(res.profile);
      // Sync the in-memory session: first/last name & photo show in TopNav
      adoptUser({
        ...user,
        first_name: res.profile.first_name,
        last_name: res.profile.last_name,
        phone: res.profile.phone_no,
        photo_url: res.profile.image_url,
      });
      toast.success("Profile saved");
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
      return false;
    } finally {
      setSaving(false);
    }
  }

  const isAdmin = isAdminUser(user);
  const approved = Number(profile?.approved) === 1;
  const needsActivation = !!profile && !approved && !isAdmin;
  const showSetup = approved || isAdmin || needsActivation;
  const [activationGone, setActivationGone] = useState(false);
  const [homeTabSaving, setHomeTabSaving] = useState(false);
  async function toggleHomeTab(on) {
    setHomeTabSaving(true);
    setForm((f) => ({ ...(f || {}), home_tab: on }));
    try {
      const res = await saveProfile({ user_record_id: recId, home_tab: on });
      setProfile(res.profile);
      toast.success(on ? "Home tab is now on your dealer page" : "Home tab hidden from your dealer page");
    } catch (e) {
      setForm((f) => ({ ...(f || {}), home_tab: !on }));
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setHomeTabSaving(false); }
  }
  function onActivated() {
    setActivationGone(true);
    adoptUser({ ...user, approved: 1 });
    getProfile(recId).then((res) => { setProfile(res.profile); setForm((f) => ({ ...(f || {}), approved: 1 })); getSetupState(recId).then(setSetupState).catch(() => setSetupState({})); setTab("setup"); }).catch(() => {});
  }
  const onboarding = useSetupReport(recId);
  const [setupState, setSetupState] = useState(null);
  const setupLaunched = !!setupState?.launched;
  const [tab, setTab] = useState("");
  useEffect(() => {
    if (!profile || tab) return;
    const q = new URLSearchParams(window.location.search).get("tab");
    const valid = ["setup", "general", "gallery", "design", "settings"];
    const pick = (launched) => setTab(valid.includes(q) ? q : isAdmin ? "setup" : Number(profile.approved) === 1 && !launched ? "setup" : "design");
    if (Number(profile.approved) !== 1 && !isAdmin) { setSetupState({}); setTab("setup"); return; }
    if (isAdmin) { setSetupState({}); pick(true); return; }
    getSetupState(recId)
      .then((st) => { setSetupState(st); pick(!!st.launched); })
      .catch(() => { setSetupState({}); pick(false); });
  }, [profile, recId, tab]);

  if (!user) return null;
  if (loading || !form) {
    return (
      <div className="px-4 pt-10 flex flex-col items-center text-zinc-500">
        <Loader2 className="animate-spin mb-2" />
        <span className="text-sm">Loading profile…</span>
      </div>
    );
  }

  const displayName =
    `${form.first_name || ""} ${form.last_name || ""}`.trim() ||
    form.email_id ||
    "Dealer";

  return (
    <div className="px-4 pt-6 pb-32 sm:pb-12" data-testid="profile-page">
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">
          Manage
        </h1>
        <Button
          onClick={save}
          disabled={!isDirty || saving}
          className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold disabled:opacity-50"
          data-testid="profile-save-btn"
        >
          {saving ? (
            <Loader2 className="animate-spin" size={16} />
          ) : (
            <>
              <Save size={16} className="mr-1.5" /> Save
            </>
          )}
        </Button>
      </div>

      {(() => {
        const trig = "flex-1 min-w-[64px] whitespace-nowrap rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs font-medium flex items-center justify-center gap-1";
        const tabsList = (
          <TabsList className="flex w-full overflow-x-auto mb-3 bg-zinc-100 rounded-xl p-1 h-11 gap-1">
            {showSetup && (
              <TabsTrigger value="setup" className={`${trig} min-w-[72px]`} data-testid="profile-tab-setup">
                {setupLaunched ? <Check size={13} className="text-emerald-600" /> : <Rocket size={13} className="text-violet-600" />} Setup
              </TabsTrigger>
            )}
            <TabsTrigger value="general" className={trig} data-testid="profile-tab-general">Profile</TabsTrigger>
            <TabsTrigger value="gallery" className={trig} data-testid="profile-tab-gallery">Gallery</TabsTrigger>
            <TabsTrigger value="design" className={`${trig} min-w-[96px]`} data-testid="profile-tab-design">
              <Sparkles size={13} className="text-violet-600" /> Marketing AI
            </TabsTrigger>
            <TabsTrigger value="settings" className={trig} data-testid="profile-tab-settings">Settings</TabsTrigger>
          </TabsList>
        );
        return (
      <Tabs value={tab} onValueChange={setTab} className="w-full">
        {/* Identity header + date-range hero (report lives inside, collapsible) + tab bar */}
        <DealerDashboard
          recId={recId}
          companyName={form.company_name || displayName}
          logoUrl={form.dealer_logo || form.image_url}
          personName={displayName}
          personEmail={form.email_id || user.email}
          roleLabel={(user.role_name || user.role || "dealer").toString()}
          avatarUrl={form.image_url}
          afterHeader={tabsList}
          collapsibleReport
          extraKpi={onboarding ? {
            label: "Dealer onboarding",
            value: `${onboarding.summary.launched} live · ${onboarding.summary.total} dealers`,
            sub: `${onboarding.summary.in_progress} in progress · ${onboarding.summary.not_started} not started`,
          } : null}
          reportExtra={<>
            <SetupReportInline recId={recId} data={onboarding} />
            {isAdmin && <ActivationReport recId={recId} />}
          </>}
        />

        {/* ====== SETUP (activation popup for unapproved dealers, then the setup steps) ====== */}
        {needsActivation && !activationGone && <ActivationPopup recId={recId} onActivated={onActivated} />}
        {showSetup && (
          <TabsContent value="setup" className="space-y-4">
            <SetupTab
              recId={recId}
              form={form}
              setForm={setForm}
              bind={bind}
              save={save}
              saving={saving}
              isDirty={isDirty}
              slug={profile?.slug}
              email={user.email_id || user.email}
              setupState={setupState}
              onSetupState={setSetupState}
              onComplete={() => setTab("design")}
            />
          </TabsContent>
        )}

        {/* ====== GENERAL ====== */}
        <TabsContent value="general" className="space-y-4">
          <Section title="Personal">
            <Grid2>
              <Field label="First name" testId="field-first-name">
                <Input {...bind("first_name")} className={INPUT_CLS} />
              </Field>
              <Field label="Last name" testId="field-last-name">
                <Input {...bind("last_name")} className={INPUT_CLS} />
              </Field>
            </Grid2>
            <Field label="Company name" icon={Building2} testId="field-company-name">
              <Input {...bind("company_name")} className={INPUT_CLS} />
            </Field>
            <Field label="Phone" icon={Phone} testId="field-phone">
              <Input
                inputMode="tel"
                {...bind("phone_no")}
                className={INPUT_CLS}
              />
            </Field>
            <Field
              label="Email (read-only)"
              icon={Mail}
              hint="Contact support to change your email"
              testId="field-email-readonly"
            >
              <Input
                value={form.email_id}
                disabled
                className={`${INPUT_CLS} bg-zinc-50 text-zinc-500`}
              />
            </Field>
          </Section>

          <Section title="Photo & logo">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-[11px] font-medium text-zinc-500 mb-1">Profile photo</div>
                <ImageUpload
                  label="Profile photo"
                  value={form.image_url}
                  onChange={(url) => setForm((f) => ({ ...f, image_url: url }))}
                  shape="circle"
                  testId="image-upload-photo"
                  userRecordId={recId}
                  kind="photo"
                />
              </div>
              <div>
                <div className="text-[11px] font-medium text-zinc-500 mb-1">Company logo</div>
                <ImageUpload
                  label="Company logo"
                  value={form.dealer_logo}
                  onChange={(url) => setForm((f) => ({ ...f, dealer_logo: url }))}
                  shape="square"
                  testId="image-upload-logo"
                  userRecordId={recId}
                  kind="logo"
                />
              </div>
            </div>
          </Section>

          <Section title="Dealer details">
            <HandleBlock form={form} setForm={setForm} slug={profile?.slug || form?.slug} recId={recId} />
            <Grid2>
              <Field label="Role (read-only)" testId="field-role">
                <Input
                  value={profile?.role_name || form.role_name || "—"}
                  disabled
                  className={`${INPUT_CLS} bg-zinc-50 text-zinc-500`}
                />
              </Field>
              <Field label="Commission % (read-only)" testId="field-commission">
                <Input
                  value={
                    profile?.commission_perc != null
                      ? `${profile.commission_perc}%`
                      : "—"
                  }
                  disabled
                  className={`${INPUT_CLS} bg-zinc-50 text-zinc-500`}
                />
              </Field>
            </Grid2>
            <Grid2>
              <Field label="Tax exempt (read-only)" testId="field-tax">
                <Input
                  value={profile?.tax_exempted ? "Exempt" : "Not exempt"}
                  disabled
                  className={`${INPUT_CLS} bg-zinc-50 text-zinc-500`}
                />
              </Field>
              <Field label="Approval (read-only)" testId="field-approved">
                <Input
                  value={profile?.approved ? "Approved" : "Pending"}
                  disabled
                  className={`${INPUT_CLS} bg-zinc-50 text-zinc-500`}
                />
              </Field>
            </Grid2>
            <div className="text-[11px] text-zinc-500 -mt-1">
              Role, commission, tax & approval are set by an admin.
            </div>
          </Section>

          <Section title="Billing address">
            <Field label="Street" icon={MapPin} testId="field-address">
              <Input {...bind("address")} className={INPUT_CLS} />
            </Field>
            <Field label="Apt / suite" testId="field-address2">
              <Input {...bind("address2")} className={INPUT_CLS} />
            </Field>
            <Grid2>
              <Field label="City" testId="field-city">
                <Input {...bind("city")} className={INPUT_CLS} />
              </Field>
              <Field label="State" testId="field-state">
                <Input {...bind("state")} className={INPUT_CLS} />
              </Field>
            </Grid2>
            <Grid2>
              <Field label="ZIP / postcode" testId="field-postcode">
                <Input {...bind("postcode")} className={INPUT_CLS} />
              </Field>
              <Field label="Country" testId="field-country">
                <Input {...bind("country")} className={INPUT_CLS} />
              </Field>
            </Grid2>
          </Section>

          <Section title="Shipping address">
            <label
              className="flex items-center gap-2.5 cursor-pointer select-none"
              data-testid="ship-same-as-billing-label"
            >
              <input
                type="checkbox"
                checked={sameShip}
                onChange={(e) => {
                  const v = e.target.checked;
                  setSameShip(v);
                  setForm((f) => ({ ...f, ship_same_as_billing: v ? 1 : 0 }));
                }}
                className="h-4 w-4 accent-emerald-600"
                data-testid="ship-same-as-billing"
              />
              <span className="text-sm font-medium text-zinc-800">
                Same as billing address
              </span>
            </label>

            {!sameShip && (
              <div className="space-y-3 pt-1" data-testid="shipping-fields">
                <Field label="Street" icon={MapPin} testId="field-ship-address">
                  <Input {...bind("ship_address")} className={INPUT_CLS} />
                </Field>
                <Field label="Apt / suite" testId="field-ship-address2">
                  <Input {...bind("ship_address2")} className={INPUT_CLS} />
                </Field>
                <Grid2>
                  <Field label="City" testId="field-ship-city">
                    <Input {...bind("ship_city")} className={INPUT_CLS} />
                  </Field>
                  <Field label="State" testId="field-ship-state">
                    <Input {...bind("ship_state")} className={INPUT_CLS} />
                  </Field>
                </Grid2>
                <Grid2>
                  <Field label="ZIP / postcode" testId="field-ship-postcode">
                    <Input {...bind("ship_postcode")} className={INPUT_CLS} />
                  </Field>
                  <Field label="Country" testId="field-ship-country">
                    <Input {...bind("ship_country")} className={INPUT_CLS} />
                  </Field>
                </Grid2>
              </div>
            )}
          </Section>
          {/* ── Designer / content (collapsible to save space) ── */}
          <CollapsibleSection title="Brand bio">
            <BioBlock form={form} setForm={setForm} bind={bind} recId={recId} />
          </CollapsibleSection>

          <CollapsibleSection title="About us / Custom HTML widget">
            <AboutUsBlock form={form} setForm={setForm} slug={profile?.slug || form?.slug} recId={recId} />
          </CollapsibleSection>

          <CollapsibleSection title="Home page widget">
            <HomeWidgetBlock form={form} />
          </CollapsibleSection>

          {form?.home_tab && (
            <CollapsibleSection title="Home page tabs">
              <HomeTabsBlock recId={recId} slug={profile?.slug || form?.slug} />
            </CollapsibleSection>
          )}

          <CollapsibleSection title="Web & social">
            <WebSocialBlock bind={bind} />
          </CollapsibleSection>

          <CollapsibleSection title="Payment handles (for invoices)">
            <PaymentHandlesBlock bind={bind} />
          </CollapsibleSection>
        </TabsContent>

        {/* ====== GALLERY ====== */}
        <TabsContent value="gallery" className="space-y-4">
          <Section title="Media gallery">
            <p className="text-xs text-zinc-500 -mt-1 mb-1">
              Upload photos, videos, audio or PDFs. Each file gets a public
              <span className="font-mono"> uploads.orevital.com </span>URL you
              can copy into emails, widgets or product pages.
            </p>
            <GalleryTab userRecordId={recId} />
          </Section>
        </TabsContent>

        {/* ====== MARKETING AI (Emails + Knowledge Base) ====== */}
        <TabsContent value="design" className="space-y-4">
          <MarketingHub userRecordId={recId} userId={String(user?.user_id || user?.id || "")} isAdmin={isAdmin} />
        </TabsContent>

        {/* ====== SETTINGS ====== */}
        <TabsContent value="settings" className="space-y-4">
          <Section title="Dealer page">
            <HomeTabToggle checked={form?.home_tab} saving={homeTabSaving} onChange={toggleHomeTab} />
          </Section>
          {isAdmin && (
            <Section title="Integrations">
              <IntegrationsSection recId={recId} />
            </Section>
          )}
          <Section title="Campaign sending">
            <EmailSending
              userRecordId={recId}
              defaultEmail={user.email_id || user.email}
            />
            <div className="h-3" />
            <TwilioSettings userRecordId={recId} />
            <DealerInstagramSettings userRecordId={recId} />
            {isAdminUser(user) && (
              <>
                <SocialSettings userRecordId={recId} />
                <InstagramImportPanel userRecordId={recId} />
              </>
            )}
          </Section>

          <ChangePasswordCard userRecordId={recId} />

          <Button
            variant="outline"
            className="w-full h-12 rounded-xl border-zinc-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
            onClick={logout}
            data-testid="profile-logout-btn"
          >
            <LogOut size={18} className="mr-2" /> Sign out
          </Button>
        </TabsContent>
      </Tabs>
        );
      })()}

      {/* Floating Save FAB (mobile, when dirty) */}
      {isDirty && (
        <div className="fixed bottom-20 sm:bottom-4 inset-x-0 z-20 px-4 pointer-events-none">
          <div className="max-w-2xl mx-auto pointer-events-auto">
            <Button
              onClick={save}
              disabled={saving}
              className="w-full h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg font-semibold"
              data-testid="profile-save-fab"
            >
              {saving ? (
                <Loader2 className="animate-spin" size={16} />
              ) : (
                <>
                  <Save size={16} className="mr-1.5" /> Save changes
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}


function initials(fn, ln) {
  return `${(fn || "?")[0]?.toUpperCase() || ""}${
    (ln || "")[0]?.toUpperCase() || ""
  }`;
}

function ProfileAvatar({ url, initials }) {
  if (url) {
    return (
      <img
        src={url}
        alt="Profile"
        className="w-16 h-16 rounded-full object-cover border-2 border-white shadow"
        data-testid="profile-photo"
        onError={(e) => {
          e.currentTarget.replaceWith(
            Object.assign(document.createElement("div"), {
              className:
                "w-16 h-16 rounded-full bg-violet-600 text-white flex items-center justify-center text-xl font-semibold",
              innerText: initials,
            })
          );
        }}
      />
    );
  }
  return (
    <div
      className="w-16 h-16 rounded-full bg-violet-600 text-white flex items-center justify-center text-xl font-semibold"
      data-testid="profile-avatar-initials"
    >
      {initials || "?"}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <Card className="rounded-2xl border-zinc-200 p-4 space-y-3">
      <div className="text-xs uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-1.5">
        <Pencil size={11} /> {title}
      </div>
      {children}
    </Card>
  );
}

function CollapsibleSection({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const tid = title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  return (
    <Card className="rounded-2xl border-zinc-200 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-2 p-4 hover:bg-zinc-50 transition-colors"
        data-testid={`profile-collapse-${tid}`}
      >
        <span className="text-xs uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-1.5">
          <Pencil size={11} /> {title}
        </span>
        {open ? (
          <Minus size={16} className="text-zinc-400" />
        ) : (
          <Plus size={16} className="text-zinc-400" />
        )}
      </button>
      {open && <div className="px-4 pb-4 space-y-3">{children}</div>}
    </Card>
  );
}
